import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Chip,
  Card,
  CardMedia,
  Breadcrumbs,
  Link as MuiLink,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";

// Icons
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ApartmentIcon from "@mui/icons-material/Apartment";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ConstructionIcon from "@mui/icons-material/Construction";
import BusinessIcon from "@mui/icons-material/Business";
import MapIcon from "@mui/icons-material/Map";
import { Bathroom } from "@mui/icons-material";
import SingleBedIcon from "@mui/icons-material/SingleBed";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { BiInfoCircle } from "react-icons/bi";
import SEO from "../../../utils/SeoComponent";

const FeaturedPropertyDetailPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [areaData, setAreaData] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const DESCO_API_BASE = import.meta.env.VITE_DESCO_API_BASE;

  useEffect(() => {
    const fetchAreaData = async () => {
      try {
        const response = await axios.get(
          `${DESCO_API_BASE}/properties/featured/${slug}`
        );

        setAreaData(response.data.data);
      } catch (error) {
        console.error("Error fetching area data:", error);
      }
    };

    if (slug) {
      fetchAreaData();
    }
  }, [slug]);

  const theme = createTheme({
    palette: {
      primary: {
        main: "#d32f2f", // Red color
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
  });

  const areaDetailData = areaData?.[0];
  const latitude = 25.1972;
  const longitude = 55.2744;
  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === areaDetailData?.gallery_images.length - 1
        ? 0
        : prevIndex + 1
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0
        ? areaDetailData?.gallery_images.length - 1
        : prevIndex - 1
    );
  };

  const handleThumbnailClick = (index) => {
    setCurrentImageIndex(index);
  };

  const sanitizedHTML = DOMPurify.sanitize(areaDetailData?.description);

  const metaTitle = "test";
  const metaDescription = "testing description";

  return (
    <>
      <SEO
        slug={"featuredpropertydetailpage"}
        title={metaTitle}
        description={metaDescription}
      />
      <Container
        maxWidth="lg"
        sx={{
          my: 4,
          paddingTop: {
            xs: "60px", // below 600px
            sm: "80px", // 600px and above
            md: "100px", // 900px and above
            lg: "120px", // 1200px and above
          },
          fontFamily: "'Poppins', sans-serif",
        }}
      >
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 3 }}
        >
          <MuiLink component={Link} to="/" color="inherit">
            Home
          </MuiLink>
          <Typography color="text.primary">{areaDetailData?.title}</Typography>
        </Breadcrumbs>

        {/* Area Title */}
        <Typography
          variant="h3"
          component="h1"
          sx={{
            mb: 2,
            fontWeight: 700,
            color: "#C10E14",
          }}
        >
          {areaDetailData?.title}
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{
            mb: 4,
            display: "flex",
            alignItems: "center",
            color: "text.secondary",
          }}
        >
          <LocationOnIcon sx={{ mr: 1 }} />
          <a style={{ color: "black" }} href={areaDetailData?.location_url}>
            {areaDetailData?.location}
          </a>
        </Typography>

        <Grid container spacing={4}>
          {/* Images Section */}
          <Grid item xs={12}>
            <Paper
              elevation={3}
              sx={{ mb: 4, overflow: "hidden", borderRadius: 2 }}
            >
              <Box sx={{ position: "relative" }}>
                {/* Main Image */}
                <Box
                  sx={{
                    width: "100%",
                    height: { xs: "250px", sm: "400px", md: "500px" },
                    position: "relative",
                    "&:hover .navigation": {
                      opacity: 1,
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    image={
                      areaDetailData?.gallery_images?.[currentImageIndex]?.files
                    }
                    alt={areaDetailData?.gallery_images?.[currentImageIndex]}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />

                  {/* Image Caption */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      width: "100%",
                      bgcolor: "rgba(0,0,0,0.6)",
                      color: "white",
                      p: 2,
                    }}
                  >
                    <Typography variant="body1">
                      {/* {areaData?.images[currentImageIndex].caption} */}
                    </Typography>
                  </Box>

                  {/* Navigation Arrows */}
                  <Box
                    className="navigation"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      opacity: { xs: 1, md: 0 },
                      transition: "opacity 0.3s ease",
                    }}
                  >
                    <IconButton
                      onClick={handlePrevImage}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.7)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                        ml: 1,
                      }}
                    >
                      <ArrowBackIosNewIcon />
                    </IconButton>

                    <IconButton
                      onClick={handleNextImage}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.7)",
                        "&:hover": { bgcolor: "rgba(255,255,255,0.9)" },
                        mr: 1,
                      }}
                    >
                      <ArrowForwardIosIcon />
                    </IconButton>
                  </Box>
                </Box>

                {/* Image Thumbnails */}
                <Box
                  sx={{
                    display: "flex",
                    p: 2,
                    overflowX: "auto",
                    "&::-webkit-scrollbar": {
                      height: "8px",
                    },
                  }}
                >
                  {areaDetailData?.gallery_images?.map((image, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: "100px",
                        height: "70px",
                        flexShrink: 0,
                        mr: 1,
                        border:
                          index === currentImageIndex
                            ? "3px solid #C10E14"
                            : "3px solid transparent",
                        cursor: "pointer",
                      }}
                      onClick={() => handleThumbnailClick(index)}
                    >
                      <CardMedia
                        component="img"
                        image={image.files}
                        alt={image}
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Description and Details */}
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                border: 1,
                borderColor: "#e7e7e7",
                p: 3,
                mb: 4,
                borderRadius: 2,
                transition: "all 0.3s ease",
                "&:hover": {
                  elevation: 6,
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[6],
                },
              }}
            >
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                Property Overview
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={6} sm={3}>
                  <Card
                    elevation={0}
                    sx={{
                      // borderRight: { sm: "1px solid #e0e0e0" },
                      height: "100%",
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "#f9fafb",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <ApartmentIcon
                      sx={{ fontSize: 30, mb: 1, color: "#C10E14" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {areaDetailData?.type || "Residential"}
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "#f9fafb",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <ConstructionIcon
                      sx={{ fontSize: 30, mb: 1, color: "#C10E14" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Area
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {areaDetailData?.square_feet + "sqft"}
                    </Typography>
                  </Card>
                </Grid>

                <Grid item xs={6} sm={3}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "#f9fafb",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 6,
                      },
                    }}
                  >
                    <BusinessIcon
                      sx={{ fontSize: 30, mb: 1, color: "#C10E14" }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Developer
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {areaDetailData?.developer || "URBAN"}
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </Paper>

            <Divider
              sx={{
                my: 2.5,
                opacity: 0.7,
                "&::before, &::after": {
                  borderColor: "#C10E14",
                },
              }}
            />

            {/* Property Description */}
            <Paper
              elevation={0.5}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 2,
                backgroundColor: "#fff1f1",
                transition: "all 0.3s ease",
                "&:hover": {
                  elevation: 6,
                  transform: "translateY(-4px)",
                  boxShadow: theme.shadows[6],
                },
              }}
            >
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                About{" "}
                <span style={{ color: "#C10E14" }}>
                  {areaDetailData?.title}
                </span>
              </Typography>
              <Typography variant="body1" paragraph>
                {areaDetailData?.short_description}
              </Typography>
              <Divider sx={{ my: 3 }} />
              <Typography
                variant="h5"
                sx={{
                  mb: 2,
                  fontWeight: 600,
                }}
              >
                Area Overview
              </Typography>

              <div
                style={{ backgroundColor: "#fff1f1" }}
                dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
              />
            </Paper>

            <Divider
              sx={{
                my: 2.5,
                opacity: 0.7,
                "&::before, &::after": {
                  borderColor: "#C10E14",
                },
              }}
            />

            {/* Area Stats */}
            {/* <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Typography
              variant="h5"
              sx={{
                mb: 3,
              }}
            >
              Area Statistics
            </Typography>
            <Grid container spacing={3}>
              {areaData?.stats?.map((stat, index) => (
                <Grid item xs={6} sm={3} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="#C10E14"
                      sx={{ fontWeight: "bold" }}
                    >
                      {stat?.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat?.label}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper> */}

            {/* Unit Breakdown Section */}
            <Paper
              elevation={1}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 2,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
            >
              <Box sx={{ mt: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ mb: 2 }}
                >
                  Unit Breakdown:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: "center",

                        backgroundColor: "#f9fafb",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 6,
                        },
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Bedroom
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#C10E14" }}>
                        {areaDetailData?.room}
                      </Typography>
                      <SingleBedIcon sx={{ color: "#C10E14" }} />
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Card
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: "center",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: 6,
                        },
                        backgroundColor: "#f9fafb",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Bathrooms
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#C10E14" }}>
                        {areaDetailData?.bathroom}
                      </Typography>
                      <Bathroom sx={{ color: "#C10E14" }} />
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* Amenities */}
            <Paper
              elevation={1}
              sx={{
                p: 3,
                mb: { xs: 4, md: 0 },
                borderRadius: 2,
                transition: "all 0.2s ease",
                "&:hover": {
                  boxShadow: 3,
                },
              }}
            >
              <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                Amenities
              </Typography>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                {areaDetailData?.amenities?.map((amenity, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 1,
                        transition: "transform 0.2s ease",
                        "&:hover": {
                          transform: "translateX(5px)",
                        },
                      }}
                    >
                      <CheckCircleOutlineIcon
                        sx={{
                          color: "#C10E14",
                          mr: 1.5,
                          fontSize: 20,
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.primary",
                          fontWeight: 500,
                        }}
                      >
                        {amenity?.title || `Amenity ${index + 1}`}{" "}
                        {amenity?.value}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <ThemeProvider theme={theme}>
              <Paper
                elevation={2}
                sx={{ maxWidth: 400, borderRadius: "8px", mb: 4 }}
              >
                {/* Header Section */}
                <Box
                  sx={{
                    bgcolor: "#fff1f1",
                    p: 2,
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                  }}
                >
                  <Typography variant="h5" fontWeight="bold">
                    Price Information
                  </Typography>
                </Box>

                {/* Price Section */}
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Starting price
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    color="primary.main"
                    sx={{ my: 1 }}
                  >
                    AED {areaDetailData?.price}
                  </Typography>
                </Box>

                {/* Payment Plan Section */}
                <Box sx={{ px: 3, pb: 3 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight="medium"
                    sx={{ mb: 2 }}
                  >
                    Payment Plan
                  </Typography>

                  <Grid container spacing={2}>
                    {/* Down Payment */}
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle2">
                              Down Payment
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {areaDetailData?.down_payment}% of total amount
                            </Typography>
                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              sx={{ mt: 1 }}
                            >
                              AED {areaDetailData?.down_payment_price}
                            </Typography>
                          </Box>
                          <IconButton size="small">
                            <BiInfoCircle fontSize="small" color="action" />
                          </IconButton>
                        </Box>
                      </Paper>
                    </Grid>

                    {/* Monthly Payment */}
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle2">
                              Monthly Payment
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              AED {areaDetailData?.monthly_payment} per month
                            </Typography>
                          </Box>
                          <IconButton size="small">
                            <BiInfoCircle fontSize="small" color="action" />
                          </IconButton>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </ThemeProvider>

            <Paper
              elevation={2}
              sx={{ p: 3, mb: 4, borderRadius: 2, backgroundColor: "#f9fafb" }}
            >
              <Typography variant="h5" sx={{ mb: 2 }}>
                Location
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <MapIcon sx={{ mr: 1, color: "#C10E14" }} />
                <Typography variant="body1">
                  {areaDetailData?.location}
                </Typography>
              </Box>
              <Box
                sx={{
                  height: "200px",
                  mb: 2,
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                <iframe
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  style={{ border: 0 }}
                  src={`https://maps.google.com/maps?q=${latitude},${longitude}&z=15&output=embed`}
                  allowFullScreen
                  title="Location Map"
                ></iframe>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<LocationOnIcon />}
                  href={areaDetailData?.location_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#C10E14",
                    borderColor: "#C10E14",
                    "&:hover": { borderColor: "#a60c11" },
                  }}
                >
                  View on Maps
                </Button>
              </Box>
            </Paper>

            {/* CTA */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: "#C10E14",
                color: "primary.contrastText",
              }}
            >
              <Typography variant="h6" sx={{ mb: 2 }}>
                Interested in {areaDetailData?.title}?
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Explore available properties or get in touch with our agents for
                more information.
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  component={Link}
                  to={`/buy`}
                  sx={{
                    bgcolor: "white",
                    color: "#C10E14",
                    "&:hover": { bgcolor: "grey.100" },
                  }}
                >
                  View Properties
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  component={Link}
                  to="/contact-us"
                  sx={{
                    color: "white",
                    borderColor: "white",
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Contact Agent
                </Button>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default FeaturedPropertyDetailPage;
